import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_ENDPOINTS } from '../config/api-endpoints';
import { Staff } from '../models';

@Injectable({
  providedIn: 'root'
})
export class StaffService {

  constructor(private http: HttpClient) { }

  getStaffList(): Observable<Staff[]> {
    return this.http.get<any>(`staff:${API_ENDPOINTS.STAFF.LIST}`).pipe(
      map(response => response.content || [])
    );
  }

  /**
   * One page of the roster, with the total so a paginator can offer the rest.
   *
   * getStaffList() asks for no page and keeps only `content`, so it returns the
   * server's first page — twenty people, Spring's default — and discards the
   * count. A bar with more than twenty staff simply could not see the rest:
   * the screen rendered a paginator that was wired to nothing.
   */
  getStaffPage(page: number, size: number): Observable<{ content: Staff[]; totalElements: number }> {
    return this.http
      // Newest first. The server sorts by nothing, so the roster came back in
      // insertion order: a bar owner who had just hired someone did not see
      // them — the new person was the LAST row, on whatever page that fell on.
      // The same omission put the orders staff must act on behind a page of
      // orders from last week.
      .get<any>(`staff:${API_ENDPOINTS.STAFF.LIST}`, { params: { page, size, sort: 'id,desc' } })
      .pipe(map(r => ({
        content: r?.content ?? [],
        // A server that ever returns a bare array still works here.
        totalElements: r?.totalElements ?? (Array.isArray(r) ? r.length : (r?.content?.length ?? 0)),
      })));
  }

  getStaff(id: string | number): Observable<Staff> {
    return this.http.get<Staff>(`staff:${API_ENDPOINTS.STAFF.DETAIL(id)}`);
  }

  getActiveStaff(): Observable<Staff[]> {
    return this.http.get<any>(`staff:${API_ENDPOINTS.STAFF.ACTIVE}`);
  }

  getStaffByEmail(email: string): Observable<Staff> {
    return this.http.get<Staff>(`staff:${API_ENDPOINTS.STAFF.BY_EMAIL(email)}`);
  }

  getStaffByRole(role: string): Observable<Staff[]> {
    return this.http.get<Staff[]>(`staff:${API_ENDPOINTS.STAFF.BY_ROLE(role)}`);
  }

  createStaff(staff: Staff): Observable<Staff> {
    return this.http.post<Staff>(`staff:${API_ENDPOINTS.STAFF.CREATE}`, staff);
  }

  updateStaff(id: string | number, staff: Partial<Staff>): Observable<Staff> {
    return this.http.put<Staff>(`staff:${API_ENDPOINTS.STAFF.UPDATE(id)}`, staff);
  }

  deleteStaff(id: string | number): Observable<void> {
    return this.http.delete<void>(`staff:${API_ENDPOINTS.STAFF.DELETE(id)}`);
  }

  updateStaffStatus(id: string | number, isActive: boolean): Observable<Staff> {
    return this.http.put<Staff>(`staff:${API_ENDPOINTS.STAFF.STATUS(id)}`, null, {
      params: { isActive: isActive.toString() }
    });
  }

  getStaffPerformance(id: string | number): Observable<Staff> {
    return this.http.get<Staff>(`staff:${API_ENDPOINTS.STAFF.PERFORMANCE(id)}`);
  }

  /** Start a shift — the clock-in is what makes hoursWorked a measurement. */
  clockIn(id: string | number): Observable<Staff> {
    return this.http.post<Staff>(`staff:${API_ENDPOINTS.STAFF.CLOCK_IN(id)}`, null);
  }

  /** End a shift; the elapsed time is added to hoursWorked server-side. */
  clockOut(id: string | number): Observable<Staff> {
    return this.http.post<Staff>(`staff:${API_ENDPOINTS.STAFF.CLOCK_OUT(id)}`, null);
  }

  /** Record tips taken; added to the running total, not replacing it. */
  addTips(id: string | number, tips: number): Observable<Staff> {
    return this.http.post<Staff>(`staff:${API_ENDPOINTS.STAFF.TIPS(id)}`, null,
      { params: { tips: tips.toString() } });
  }

  updateStaffPerformance(id: string | number, hoursWorked: number, tips: number): Observable<Staff> {
    return this.http.put<Staff>(`staff:${API_ENDPOINTS.STAFF.PERFORMANCE(id)}`, null, {
      params: { hoursWorked: hoursWorked.toString(), tips: tips.toString() }
    });
  }

  searchStaff(filters: {
    name?: string;
    role?: string;
    isActive?: boolean;
    page?: number;
    size?: number;
  }): Observable<any> {
    let params = new HttpParams();
    if (filters.name) params = params.set('name', filters.name);
    if (filters.role) params = params.set('role', filters.role);
    if (filters.isActive != null) params = params.set('isActive', filters.isActive.toString());
    params = params.set('page', (filters.page ?? 0).toString());
    params = params.set('size', (filters.size ?? 20).toString());
    return this.http.get<any>(`staff:${API_ENDPOINTS.STAFF.SEARCH}`, { params });
  }

  getRecentlyActiveStaff(since: string): Observable<Staff[]> {
    return this.http.get<Staff[]>(`staff:${API_ENDPOINTS.STAFF.RECENTLY_ACTIVE}`, {
      params: { since }
    });
  }

  getTopPerformersByTips(page: number = 0, size: number = 5): Observable<Staff[]> {
    return this.http.get<Staff[]>(`staff:${API_ENDPOINTS.STAFF.TOP_PERFORMERS_TIPS}`, {
      params: { page: page.toString(), size: size.toString() }
    });
  }

  getTopPerformersByHours(page: number = 0, size: number = 5): Observable<Staff[]> {
    return this.http.get<Staff[]>(`staff:${API_ENDPOINTS.STAFF.TOP_PERFORMERS_HOURS}`, {
      params: { page: page.toString(), size: size.toString() }
    });
  }

  getLowPerformers(threshold: number): Observable<Staff[]> {
    return this.http.get<Staff[]>(`staff:${API_ENDPOINTS.STAFF.LOW_PERFORMERS}`, {
      params: { threshold: threshold.toString() }
    });
  }

  getStaffStatistics(): Observable<any> {
    return this.http.get<any>(`staff:${API_ENDPOINTS.STAFF.STATISTICS}`);
  }
}
